import { useEffect, useState } from "react";
import { Mail, MessageCircle, Send, CheckCircle2, Twitch, Twitter, Instagram, Youtube } from "lucide-react";
import DiscordIcon from "@/components/DiscordIcon";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import Reveal from "@/components/Reveal";
import { useUiTexts } from "@/lib/uiTexts";

const SOCIAL_ICONS = { twitch: Twitch, twitter: Twitter, instagram: Instagram, youtube: Youtube, discord: DiscordIcon };
const inputCls = "w-full rounded-lg border border-input bg-[#0B132B] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function Contact() {
  const texts = useUiTexts();
  const [info, setInfo] = useState({ email: "", discord: "", intro: "" });
  const [socials, setSocials] = useState({});
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/contact-info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/socials").then((r) => setSocials(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const activeSocials = Object.entries(socials).filter(([, url]) => url);

  return (
    <div data-testid="contact-page" className="page-enter mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{texts.contact_label}</p>
        <h1 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">{texts.contact_title}</h1>
        <p className="mt-3 max-w-2xl text-slate-400">{info.intro}</p>
      </Reveal>

      <div className="mt-12 grid gap-8 lg:grid-cols-[1fr_380px]">
        <Reveal delay={0.1}>
          <div className="card-tactical p-6 sm:p-8">
            {sent ? (
              <div className="py-10 text-center" data-testid="contact-sent">
                <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
                <h2 className="mt-4 text-2xl font-bold uppercase">Message bien reçu !</h2>
                <p className="mt-2 text-sm text-slate-400">L'équipe Emisphere vous répondra au plus vite.</p>
                <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                  data-testid="contact-new-message"
                  className="mt-6 rounded-xl border border-orange-500/40 px-5 py-2.5 text-sm font-bold uppercase text-orange-400 transition-all hover:bg-orange-500/10">
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input placeholder="Votre nom / pseudo" value={form.name} data-testid="contact-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
                  <input type="email" placeholder="Votre email" value={form.email} data-testid="contact-email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
                </div>
                <input placeholder="Sujet (commande, partenariat, recrutement...)" value={form.subject} data-testid="contact-subject"
                  onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
                <textarea placeholder="Votre message" value={form.message} rows={5} data-testid="contact-message"
                  onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} required />
                <button type="submit" disabled={loading} data-testid="contact-submit"
                  className="glow-orange flex w-fit items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:opacity-50">
                  <Send className="h-5 w-5" /> {loading ? "Envoi..." : "Envoyer le message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="card-tactical grid gap-5 p-6 sm:p-8">
            <h2 className="text-xl font-bold uppercase tracking-tight">Autres moyens</h2>
            {info.discord && (
              <a href={info.discord} target="_blank" rel="noreferrer" data-testid="contact-discord"
                className="flex items-center gap-3 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4 transition-all hover:scale-[1.02] hover:bg-orange-500/10">
                <MessageCircle className="h-6 w-6 text-orange-500" />
                <div>
                  <p className="font-heading text-lg font-bold uppercase">Discord Emisphere</p>
                  <p className="text-xs text-slate-400">Rejoindre la communauté et discuter avec l'équipe</p>
                </div>
              </a>
            )}
            {info.email && (
              <a href={`mailto:${info.email}`} data-testid="contact-email-link"
                className="flex items-center gap-3 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4 transition-all hover:scale-[1.02] hover:bg-orange-500/10">
                <Mail className="h-6 w-6 text-orange-500" />
                <div>
                  <p className="font-heading text-lg font-bold uppercase">Email direct</p>
                  <p className="text-xs text-slate-400">{info.email}</p>
                </div>
              </a>
            )}
            {activeSocials.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{texts.contact_socials_label}</p>
                <div className="mt-3 flex gap-4 text-slate-400">
                  {activeSocials.map(([key, url]) => {
                    const Icon = SOCIAL_ICONS[key];
                    return Icon ? (
                      <a key={key} href={url} target="_blank" rel="noreferrer" data-testid={`contact-social-${key}`}
                        className="transition-colors hover:text-orange-400"><Icon className="h-6 w-6" /></a>
                    ) : null;
                  })}
                </div>
              </div>
            )}
            <p className="text-xs leading-relaxed text-slate-500">
              Nous répondons généralement sous 48h. Pour toute question sur une commande en cours, indiquez votre référence de commande dans le message.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
